<template>
    <div class="field-table tw-relative tw-flex-1 tw-flex tw-flex-col tw-max-w-full tw-text-base-content">
        <neo-tab :tabs="filteredTabs" @tabChange="selectTab($event)" margin="0" :active="currentTab" v-if="creditPackSelected"> </neo-tab>

        <vue-good-table ref="table" mode="remote" :isLoading="loading" :columns="computedColumns" :rows="rows" :pagination-options="paginationOptions" @on-page-change="onPageChange" @on-sort-change="onSortChange" @on-per-page-change="onPerPageChange" :totalRows="totalRecords" :key="key">
            <template slot="table-actions">
                <button
                    v-if="Object.values(filters).length"
                    @click="clearFilters"
                    class="tw-bg-transparent tw-border tw-border-solid tw-rounded-2xl tw-px-2 tw-py-1 tw-mr-2 tw-cursor-pointer 
                    tw-border-brand tw-text-brand hover:tw-bg-brand hover:tw-text-white"
                >
                    RESET FILTER
                </button>
                <span v-else></span>
            </template>

            <template #column-filter="{ column }">
                <neo-datepicker v-if="column.field_type === 'date' && column.filterOptions && column.filterOptions.enabled" :range="true" v-model="filters[column.field]" :open-date="new Date()" placeholder="Select Date" format="DD-MMM-YYYY" class="tw-h-10 tw-rounded tw-w-full tw-overflow-hidden" ref="datePicker1" @input="updateColumnFilters()" @clear="clearDateRange(column)"> </neo-datepicker>

                <vue-multiselect ref="setFilterRef" v-else-if="column.filterOptions && column.filterOptions.enabled" placeholder="Select" v-model="filters[column.field]" :options="getFilterOptions(column.filterOptions.key)" @input="updateColumnFilters()" :multiple="false" :checkboxes="false" :limit="1" :close-on-select="true" :clear-on-select="true" :preserve-search="true" :searchable="true" :show-labels="false" class="tw-h-8 tw-w-full"> </vue-multiselect>
            </template>
            <template slot="table-row" slot-scope="props">
                <!-- Column: Unique Id -->
                <div v-if="props.column.field === 'unique_id'" @click="selectCreditPack(props.row)" class="tw-flex tw-cursor-pointer tw-text-brand">
                    {{ props.row[props.column.field] }}
                </div>

                <!-- Column: Status -->
                <div v-else-if="props.column.field === 'status'" class="tw-flex tw-justify-center">
                    <span :class="['Allocated', 'Active'].includes(props.row.status) ? 'tw-text-success' : 'tw-text-danger'">
                        {{ props.row.status }}
                    </span>
                </div>

                <!-- Date type field -->
                <div v-else-if="props.column.field_type === 'date'" class="tw-flex tw-justify-center">
                    <span v-if="props.row[props.column.field]">
                        {{ formatDateFromISO(props.row[props.column.field], "dd MMM yyyy") }}
                    </span>
                    <span v-else> - </span>
                </div>

                <!-- Default -->
                <span v-else class="tw-flex tw-justify-center"> {{ props.row[props.column.field] }} </span>
            </template>
            <div slot="loadingContent" class="tw-text-gray-500 loader-position">
                <div class="tw-bg-blue-100 tw-p-8 tw-rounded-md tw-shadow-lg tw-flex tw-flex-col tw-items-center tw-max-w-3xl tw-break-all">
                    <svg class="tw-animate-spin tw-h-8 tw-w-10" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="tw-opacity-25" cx="12" cy="12" r="10" stroke="var(--brand-color)" stroke-width="4"></circle>
                        <path class="tw-opacity-75" fill="var(--brand-color)" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <div class="tw-text-base tw-mt-4">Please wait loading ...</div>
                </div>
            </div>

            <div v-if="loading" slot="emptystate" class="tw-bg-white tw-flex tw-justify-center tw-p-4 tw-items-center" style="height: 250px"></div>
            <div v-if="!loading" slot="emptystate" class="tw-bg-white tw-flex tw-justify-center tw-p-4 tw-items-center" style="height: 250px">
                <div class="tw-flex tw-rounded-lg w-p-4 tw-mb-4 tw-text-sm tw-text-gray-500" role="alert">
                    <div><span class="font-medium"></span> No data for table</div>
                </div>
            </div>
        </vue-good-table>
    </div>
</template>
<script>
import dropdown from "@/components/dropdown-base";
import NeoInput from "@/components/input";
import Loader from "@/components/loader";
import Tab from "@/components/tab";
const VueMultiselect = () => import("@/components/vue-multiselect");
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import {DateTime} from "luxon";
import {getUserCreditPack, getTenantFiltersData, getUserFiltersData} from "../services";
import {mapActions, mapGetters} from "vuex";

export default {
    name: "credit-pack",
    title: "Credit pack",
    components: {
        dropdown,
        NeoInput,
        Loader,
        "neo-tab": Tab,
        "neo-datepicker": DatePicker,
        VueMultiselect,
    },
    data() {
        return {
            key: 0,
            currentTab: "credit-allotted",
            tabs: [
                {
                    key: "credit-allotted",
                    name: "Credit Allocated History",
                    enabled: true,
                    admin: true,
                    analyst: true,
                },
            ],
            loading: false,
            columns: [
                {
                    label: "Unique Id",
                    field: "unique_id",
                    sortable: true,
                    filterOptions: {
                        enabled: true,
                        key: "credit_pack_display_names",
                    },
                },
                {
                    label: "Pack Name",
                    field: "name",
                    sortable: true,
                    filterOptions: {
                        enabled: true,
                        key: "credit_pack_names",
                    },
                },
                {
                    label: "Credits",
                    field: "issued_credits",
                    sortable: false,
                    filterOptions: {
                        enabled: false,
                        key: "credit_pack_credits",
                    },
                },
                {
                    label: "Available Credits",
                    field: "user_available_credits",
                    sortable: false,
                    filterOptions: {
                        enabled: false,
                    },
                },
                {
                    label: "Validity Period",
                    field: "validity",
                    sortable: true,
                    filterOptions: {
                        enabled: true,
                        key: "credit_pack_validity",
                    },
                },
                {
                    label: "Start Date",
                    field: "activated_at",
                    field_type: "date",
                    sortable: true,
                    filterOptions: {
                        enabled: true,
                    },
                },
                {
                    label: "Expiry Date",
                    field: "valid_till",
                    field_type: "date",
                    sortable: true,
                    filterOptions: {
                        enabled: true,
                    },
                },
            ],
            creditUsersColumns: [
                {
                    label: "Allocated Credits",
                    field: "issued_credits",
                    sortable: false,
                    filterOptions: {
                        enabled: false,
                    },
                },
                {
                    label: "Allocated Date",
                    field: "created_at",
                    field_type: "date",
                    sortable: true,
                    filterOptions: {
                        enabled: true,
                    },
                },
                {
                    label: "Used Credits",
                    field: "used",
                    sortable: true,
                    filterOptions: {
                        enabled: true,
                        key: "credits_used",
                    },
                },
                {
                    label: "Unused Credits",
                    field: "user_available_credits",
                    sortable: false,
                    filterOptions: {
                        enabled: false,
                    },
                },
                {
                    label: "Expiry Date",
                    field: "valid_till",
                    field_type: "date",
                    sortable: true,
                    filterOptions: {
                        enabled: true,
                    },
                },
                {
                    label: "Status",
                    field: "status",
                    sortable: false,
                    filterOptions: {
                        enabled: false,
                    },
                },
            ],
            rows: [],
            totalRecords: 0,
            filters: {},
            pagination: {
                limit: 10,
                page: 1,
            },
            paginationOptions: {
                enabled: true,
                mode: "remote",
                infoFn: (params) => `${params.firstRecordOnPage} - ${params.lastRecordOnPage} of ${params.totalRecords}`,
                perPageDropdown: [10, 20, 30, 40, 50],
                dropdownAllowAll: false,
            },
            userLists: [],
            allUsers: [],
            creditPackSelected: null,
            search: "",
        };
    },
    async mounted() {
        this.clearFilters();
        // await this.fetchData();
    },
    watch: {
        async "$route.query"(to, from) {
            if (!to.id) this.creditPackSelected = null;
            // this.clearFilters();
        },
    },
    computed: {
        ...mapGetters(["getAccountId", "getIsUserAdmin", "getFiltersData"]),
        filteredUserLists() {
            return this.userLists.filter((el) => el.user_name.toLowerCase().includes(this.search.toLowerCase()));
        },

        filteredTabs() {
            return this.tabs;
        },
        computedColumns() {
            const colName = this.creditPackSelected ? "creditUsersColumns" : "columns";
            return this?.[colName];
        },
    },
    methods: {
        ...mapActions(["setFiltersData"]),
        disabledRange(date) {
            return date > new Date();
        },

        async clearDateRange(column) {
            this.filters[column.field] = [];
            // await this.fetchData();
        },

        async selectCreditPack(row) {
            this.creditPackSelected = row;
            this.clearFilters();
            await this.getFiltersMasterData();
            this.$router
                .push({
                    path: this.$route.path,
                    query: {
                        ...this.$route.query,
                        ...{id: this.creditPackSelected.credit_pack_id},
                    },
                })
                .catch(() => {});
            this.$emit("updateBreadcrumbs");
        },

        async getFiltersMasterData() {
            try {
                let qParams;
                if (this.getAccountId)
                    qParams = {
                        tenant_id: this.getAccountId,
                        name: "credit-packs",
                        credit_pack_id: this.creditPackSelected.credit_pack_id,
                    };
                if (this.getIsUserAdmin) {
                    const filters_data = await getTenantFiltersData(qParams);
                    this.setFiltersData(filters_data?.data?.data ?? {});
                } else {
                    const filters_data = await getUserFiltersData(qParams);
                    this.setFiltersData(filters_data?.data?.data, {});
                }
            } catch {}
        },

        async fetchData() {
            try {
                this.loading = true;
                this.rows = [];
                let qParams = {
                    ...this.pagination,
                };

                const dateFields = this.computedColumns.filter((col) => col.field_type === "date")?.map((col) => col.field);
                const mapFields = {
                    name: "pack_name",
                    unique_id: "display_name",
                    users: "user_name",
                    created_at: "allocated_date",
                    activated_at: "activated",
                };

                if (this.creditPackSelected) mapFields["activated_at"] = "allocated_date";

                for (const filter in this.filters) {
                    if (dateFields.includes(filter)) {
                        this.filters[filter] = this.filters[filter].filter((el) => el);
                        if (this.filters[filter]?.length) {
                            let key = filter;
                            if (mapFields[filter]) key = mapFields[filter];

                            const start = new Date(this.filters[filter][0]);
                            const end = new Date(this.filters[filter][1]);
                            qParams = {
                                ...qParams,
                                [`${key}_from`]:
                                    start.getFullYear() +
                                    "-" +
                                    (start.getMonth() + 1).toString().padStart(2, "0") +
                                    "-" +
                                    start
                                        .getDate()
                                        .toString()
                                        .padStart(2, "0"),
                                [`${key}_to`]:
                                    end.getFullYear() +
                                    "-" +
                                    (end.getMonth() + 1).toString().padStart(2, "0") +
                                    "-" +
                                    end
                                        .getDate()
                                        .toString()
                                        .padStart(2, "0"),
                            };
                        }
                    } else if (mapFields[filter])
                        qParams = {
                            ...qParams,
                            [mapFields[filter]]: this.filters[filter],
                        };
                    else
                        qParams = {
                            ...qParams,
                            [filter]: this.filters[filter],
                        };
                }

                if (this.getAccountId)
                    qParams = {
                        ...qParams,
                        tenant_id: this.getAccountId,
                    };

                if (this.creditPackSelected)
                    qParams = {
                        ...qParams,
                        ...{credit_pack_id: this.creditPackSelected.credit_pack_id},
                    };
                const {data} = await getUserCreditPack(qParams);
                this.loading = false;
                if (this.creditPackSelected)
                    this.rows = data.data?.map((el) => ({
                        ...el,
                        ...{
                            status: el?.active ? (el?.user_available_credits === 0 ? "Exhausted" : "Allocated") : "Unallotted",
                        },
                    }));
                else this.rows = data.data;
                if (data?.page?.total) {
                    this.totalRecords = data.page.total;
                } else {
                    this.totalRecords = 0;
                }
            } catch (error) {
                this.loading = false;
                console.log(error);
            }
        },

        formatDateFromISO(dt, format = "EEE, MMM dd yyyy") {
            dt = dt.substr(0, 10);
            return DateTime.fromISO(dt).toFormat(format);
        },

        getFilterOptions(key) {
            // get unique filter options array
            return this.getFiltersData?.[key]?.filter((val) => val || val === 0) ?? [];
        },
        async updateColumnFilters() {
            this.pagination = {
                ...this.pagination,
                page: 1,
            };
            this.$refs?.["table"]?.reset();
            await this.fetchData();
        },
        async clearFilters() {
            this.filters = {};
            this.pagination = {
                limit: 10,
                page: 1,
            };
            this.$refs["table"].currentPerPage = 10;
            this.$refs?.["table"]?.reset();
            this.key++;
            await this.fetchData();
        },

        async onPageChange(params) {
            this.pagination = {
                ...this.pagination,
                page: params.currentPage,
            };
            await this.fetchData();
        },

        async onPerPageChange(params) {
            this.pagination = {
                ...this.pagination,
                limit: params.currentPerPage,
                page: 1,
            };
            await this.fetchData();
        },

        async onSortChange(params) {
            let sort_key = params[0].field;
            if (sort_key === "name") sort_key = "pack_name";
            if (sort_key === "unique_id") sort_key = "display_name";
            else if (sort_key === "users") sort_key = "user_name";
            else if (sort_key === "created_at") sort_key = "credit_allocation_date";
            else if (this.creditPackSelected && sort_key === "activated_at") sort_key = "credit_allocation_date";

            if (params[0].type == "none") {
                this.$refs["table"].sorts[0].type = "asc";
                this.pagination = {
                    ...this.pagination,
                    sort_on: sort_key,
                    sort_value: "asc",
                    page: 1,
                };
            } else {
                this.pagination = {
                    ...this.pagination,
                    sort_on: sort_key,
                    sort_value: params[0].type,
                    page: 1,
                };
            }
            await this.fetchData();
        },
    },
};
</script>
<style scoped lang="scss">
@import "@/mixins.scss";
@include tableComponent;
.field-table {
    .tabs {
        display: flex;
    }
}
.loader-position {
    position: fixed;
    top: 43%;
    bottom: 0px;
    margin-left: 40%;
}
</style>
